/**
 * A case's call-backs — FR-E10, T-2.10. Split out of `CasePanels.tsx` in Wave
 * 15C; the behaviour is unchanged.
 *
 * **Late is not missed.** `is_late` is the clock and `missed` is somebody
 * saying nobody picked up. A screen that collapsed the two would tell a firm it
 * had tried when it had not, so the row shows *Late* and the button that says
 * otherwise is a deliberate press.
 */

import { Alert, Badge, type BadgeTone, Button, EmptyState, Input } from '@fiveox/ui'
import { PhoneCall } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { RecordSection } from '../../components/page'
import { type Callback, callbackLabel, caseFlows, whenLabel } from '../../lib/api/cases'
import { useAction } from './useAction'

/** `callbackLabel`'s tone as a badge. A call-back has no kind in the status
 *  map (components/page/status.ts); these follow its rule — late or unanswered
 *  is an error, waiting is information, finished is neutral. */
const CALL_TONE: Record<string, BadgeTone> = {
  overdue: 'error',
  today: 'warning',
  soon: 'info',
  none: 'neutral',
}

export function CaseCallbacks({ caseId, canUpdate }: { caseId: string; canUpdate: boolean }) {
  const [rows, setRows] = useState<Callback[]>([])
  const [when, setWhen] = useState('')
  const [note, setNote] = useState('')
  const [outcome, setOutcome] = useState<Record<string, string>>({})
  const { error, busy, run } = useAction()

  const load = useCallback(async () => {
    setRows((await caseFlows.callbacks(caseId)).data)
  }, [caseId])

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  const act = async (fn: () => Promise<unknown>, fallback: string) => {
    if (await run(fn, fallback)) await load().catch(() => undefined)
  }

  return (
    <RecordSection
      id="case-callbacks"
      icon={<PhoneCall size={18} aria-hidden />}
      title="Call-backs"
      description="Whoever the case is assigned to is reminded a day before, an hour before, and at the time."
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        {rows.length === 0 ? (
          <EmptyState
            variant="outline"
            title="Nothing scheduled"
            description="Schedule a call when you promise your client one."
          />
        ) : (
          <ul className="divide-border divide-y">
            {rows.map((row) => {
              const label = callbackLabel(row)
              return (
                <li key={row.id} className="space-y-2 py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-foreground text-sm font-medium">
                      {whenLabel(row.scheduled_at)}
                    </span>
                    <Badge tone={CALL_TONE[label.tone] ?? 'neutral'} size="sm">
                      {/* A call still to come is labelled by its time, which the
                          line beside the badge already says. */}
                      {row.status === 'scheduled' && !row.is_late ? 'Scheduled' : label.text}
                    </Badge>
                  </div>
                  {row.note && <p className="text-muted-foreground text-sm">{row.note}</p>}
                  {row.outcome && (
                    <p className="text-foreground text-sm">Outcome: {row.outcome}</p>
                  )}
                  {canUpdate && row.status === 'scheduled' && (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <Input
                          size="sm"
                          aria-label="What happened on the call?"
                          value={outcome[row.id] ?? ''}
                          onChange={(e) => setOutcome({ ...outcome, [row.id]: e.target.value })}
                          placeholder="What happened on the call?"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            act(
                              () =>
                                caseFlows.updateCallback(row.id, {
                                  status: 'done',
                                  outcome: outcome[row.id] || undefined,
                                }),
                              'That could not be saved.',
                            )
                          }
                        >
                          Called
                        </Button>
                        {/* A deliberate press. The clock says "late"; only a
                            person can say nobody picked up. */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            act(
                              () =>
                                caseFlows.updateCallback(row.id, {
                                  status: 'missed',
                                  outcome: outcome[row.id] || undefined,
                                }),
                              'That could not be saved.',
                            )
                          }
                        >
                          No answer
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() =>
                            act(
                              () => caseFlows.updateCallback(row.id, { status: 'cancelled' }),
                              'That could not be saved.',
                            )
                          }
                        >
                          Call off
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {canUpdate && (
          <div className="border-border grid gap-3 border-t pt-4 sm:grid-cols-[14rem_minmax(0,1fr)_auto] sm:items-end">
            {/* `datetime-local` gives a value with no offset. The API refuses
                that on purpose — a time with no zone is a promise to ring
                somebody at a time nobody agreed on — so the browser's own
                offset is attached before it is sent. */}
            <Input
              size="sm"
              label="When"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <Input
              size="sm"
              label="About"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What is the call about?"
            />
            <Button
              size="sm"
              disabled={!when || busy}
              onClick={() =>
                act(async () => {
                  await caseFlows.scheduleCallback(caseId, {
                    scheduled_at: new Date(when).toISOString(),
                    note: note.trim() || undefined,
                  })
                  setWhen('')
                  setNote('')
                }, 'The call back could not be scheduled.')
              }
            >
              Schedule
            </Button>
          </div>
        )}
      </div>
    </RecordSection>
  )
}
